import api from './api';

export interface LoginCredentials {
  email: string;
  password: string;
  recaptcha_response: string;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  is_superuser: boolean;
  is_company_admin: boolean;
  company_id?: number;
  admin_companies: string[];
  user_profile_picture?: string;
  company_profile_picture?: string;
  has_active_session: boolean;
  available_languages?: string[];  // Array of language codes (e.g., ['en', 'hi', 'ar'])
  default_language?: string;  // Default language code (e.g., 'en')
}

export interface LoginResponse {
  token: string;
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  is_superuser: boolean;
  is_company_admin: boolean;
  company_id?: number;
  admin_companies: string[];
  user_profile_picture?: string;
  company_profile_picture?: string;
  has_active_session: boolean;
  available_languages?: string[];  // Array of language codes (e.g., ['en', 'hi', 'ar'])
  default_language?: string;  // Default language code (e.g., 'en')
}

class AuthService {
  private tokenKey = 'token';
  private userKey = 'user';

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await api.post('/auth/login/', credentials);
      const data = response.data;
      
      // Store token and user info
      this.setToken(data.token);
      this.setUser(data);
      
      return data;
    } catch (error) {
      throw error;
    }
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  getUser(): User | null {
    const userStr = localStorage.getItem(this.userKey);
    return userStr ? JSON.parse(userStr) : null;
  }

  setUser(user: User): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  isSuperAdmin(): boolean {
    const user = this.getUser();
    return user?.is_superuser || false;
  }

  canAccessSuperAdmin(): boolean {
    return this.isAuthenticated() && this.isSuperAdmin();
  }
}

export default new AuthService();
